/**
 * Excel-export utility — wrappar exceljs med SheetJS-kompatibelt API.
 *
 * Syfte: smartig migration från xlsx (SheetJS Community Edition) till
 * exceljs. SheetJS Community har två opatchade högar CVE:
 *  - GHSA-4r6h-8v6p-xvw6 (Prototype Pollution)
 *  - GHSA-5pgg-2g8v-p4x9 (ReDoS)
 *
 * exceljs är MIT-licensierat alternativ utan kända CVE.
 *
 * API:t här ar Smart Sheet-liknande sa befintliga xlsx-anvandare kan
 * migrera med minimal kodandring:
 *
 * GAMMAL:
 *   import * as XLSX from 'xlsx';
 *   const ws = XLSX.utils.json_to_sheet(rows);
 *   const wb = XLSX.utils.book_new();
 *   XLSX.utils.book_append_sheet(wb, ws, 'Mitt blad');
 *   XLSX.writeFile(wb, 'fil.xlsx');
 *
 * NY:
 *   import { jsonToWorkbook, writeFileFromWorkbook } from '@/utils/excelExport';
 *   const wb = await jsonToWorkbook(rows, { sheetName: 'Mitt blad' });
 *   await writeFileFromWorkbook(wb, 'fil.xlsx');
 *
 * Skillnader:
 *  - exceljs ar promise-baserat (async/await)
 *  - exceljs har inget XLSX.writeFile som triggar download direkt -
 *    vi gor det manuellt via Blob + URL.createObjectURL
 *
 * Lazy-import av exceljs sa bundle bara laddar den nar PDF/Excel faktiskt
 * exporteras (sparar ~700KB initial bundle).
 */

let _excelJsPromise = null;
async function _loadExcelJs() {
  if (!_excelJsPromise) {
    _excelJsPromise = import('exceljs').then((m) => m.default || m);
  }
  return _excelJsPromise;
}

/**
 * Skapa en exceljs Workbook från en array av JSON-objekt.
 *
 * @param {Array<Object>} rows - Array av objekt. Nycklar blir kolumnrubriker.
 * @param {Object} [options]
 * @param {string} [options.sheetName='Sheet1'] - Namn på fliken.
 * @param {Array<{key: string, header: string, width?: number}>} [options.columns] -
 *        Explicit kolumndefinition. Om utelämnad härleds från första radens nycklar.
 * @param {boolean} [options.autoWidth=true] - Auto-beräkna kolumnbredder från innehåll.
 * @returns {Promise<Workbook>} exceljs Workbook-instans.
 */
export async function jsonToWorkbook(rows, options = {}) {
  const { sheetName = 'Sheet1', columns = null, autoWidth = true } = options;
  const ExcelJS = await _loadExcelJs();

  const workbook = new ExcelJS.Workbook();
  const safeName = String(sheetName).substring(0, 31).replace(/[\\/?*[\]]/g, '_');
  const sheet = workbook.addWorksheet(safeName);

  if (!rows || rows.length === 0) {
    return workbook;
  }

  // Härled kolumner från första raden om inte explicit
  const cols = columns || Object.keys(rows[0]).map((key) => ({
    key,
    header: key,
  }));

  sheet.columns = cols.map((c) => ({
    header: c.header,
    key: c.key,
    width: c.width,
  }));

  // Lägg till rader
  rows.forEach((row) => {
    sheet.addRow(row);
  });

  // Auto-bredder om ingen explicit width given
  if (autoWidth) {
    sheet.columns.forEach((col, i) => {
      if (col.width) return;
      let max = String(col.header || '').length;
      rows.forEach((row) => {
        const val = row[col.key];
        const len = val == null ? 0 : String(val).length;
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 2, 10), 50);
    });
  }

  // Bold header
  sheet.getRow(1).font = { bold: true };

  return workbook;
}

/**
 * Skapa en tom exceljs Workbook (för manuell sheet-konfiguration).
 *
 * @returns {Promise<Workbook>}
 */
export async function createWorkbook() {
  const ExcelJS = await _loadExcelJs();
  return new ExcelJS.Workbook();
}

/**
 * Lägg till en sheet i en workbook från JSON-rader.
 * Analog till XLSX.utils.json_to_sheet + book_append_sheet.
 *
 * @param {Workbook} workbook
 * @param {Array<Object>} rows
 * @param {string} sheetName
 * @param {Object} [options]
 * @returns {Worksheet}
 */
export function appendSheetFromJson(workbook, rows, sheetName, options = {}) {
  const { columns = null, autoWidth = true } = options;
  const safeName = String(sheetName).substring(0, 31).replace(/[\\/?*[\]]/g, '_');
  const sheet = workbook.addWorksheet(safeName);

  if (!rows || rows.length === 0) {
    return sheet;
  }

  const cols = columns || Object.keys(rows[0]).map((key) => ({
    key,
    header: key,
  }));

  sheet.columns = cols.map((c) => ({
    header: c.header,
    key: c.key,
    width: c.width,
  }));

  rows.forEach((row) => sheet.addRow(row));

  if (autoWidth) {
    sheet.columns.forEach((col) => {
      if (col.width) return;
      let max = String(col.header || '').length;
      rows.forEach((row) => {
        const val = row[col.key];
        const len = val == null ? 0 : String(val).length;
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 2, 10), 50);
    });
  }

  sheet.getRow(1).font = { bold: true };
  return sheet;
}

/**
 * Skriv ut workbook till XLSX-fil och trigga browser-download.
 * Analog till XLSX.writeFile.
 *
 * @param {Workbook} workbook
 * @param {string} filename - inkl ".xlsx"
 */
export async function writeFileFromWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Läs in en XLSX-fil till en exceljs Workbook.
 * Analog till XLSX.read(arrayBuffer).
 *
 * @param {ArrayBuffer|File} input
 * @returns {Promise<Workbook>}
 */
export async function readWorkbook(input) {
  const ExcelJS = await _loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  const buffer = input instanceof File ? await input.arrayBuffer() : input;
  await workbook.xlsx.load(buffer);
  return workbook;
}

/**
 * Hämta en sheet's data som array av objekt (likt XLSX.utils.sheet_to_json).
 *
 * @param {Worksheet} sheet
 * @param {Object} [options]
 * @param {boolean} [options.firstRowAsHeader=true] - Om sant används första raden som nycklar.
 * @returns {Array<Object>}
 */
export function sheetToJson(sheet, options = {}) {
  const { firstRowAsHeader = true } = options;
  const rows = [];
  let headers = null;

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : [];
    if (firstRowAsHeader && rowNumber === 1) {
      headers = values.map((v) => String(v ?? ''));
      return;
    }
    if (firstRowAsHeader && headers) {
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = values[i] ?? '';
      });
      rows.push(obj);
    } else {
      rows.push(values);
    }
  });

  return rows;
}

/**
 * Hjälpare: filnamn med datum-suffix (Nordens YYYY-MM-DD).
 *
 * @param {string} stem - utan extension
 * @param {string} [ext='xlsx']
 */
export function dateStampedFilename(stem, ext = 'xlsx') {
  const date = new Date().toISOString().slice(0, 10);
  return `${stem}_${date}.${ext}`;
}
